import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, auditFieldsForUpdate, newId } from '../engine/context'
import { SLA_DEFS, type SlaMeta, type SlaUnit, slaMetaOf } from '../seed/wms-master-data.seed'
import { findRow, insertRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { fail, handle, idOf, pad, parse } from './c07.shared'
import {
  canWrite,
  masterLog,
  myRoles,
  num,
  registerEntity,
  requireWrite,
  sameName,
  text,
  type Usage,
  usageFromCounts
} from './masterData.shared'

/**
 * Master Data sederhana — `/api/v1/wms/master/*`:
 * - MST-11 Master Unit (`mt_unit`, kind PACK/SMALL): `unit/list`, `unit/detail`, `unit/save`.
 * - MST-09 Master Alasan NG (`mt_reject_type`, kategori ERD): `reason/list`, `reason/detail`, `reason/save`.
 * - MST-08 Master SLA (`sla_wms_setting`): `sla/list`, `sla/save` (alasan wajib, tercatat di `tx_log`).
 * - MST-10 Master Pallet Karantina (`pallet`, kind RM_QUARANTINE): `pallet/list`, `pallet/generate`.
 * Umum: `status`, `delete`, `usage` (didaftarkan di `masterData.handler.ts`).
 * Satuan dan Alasan tidak pernah dihapus, hanya dinonaktifkan (data lama harus tetap terbaca).
 */

const worldOf = (): 'RM' | 'FG' => (myRoles().some(code => code.endsWith('_fg')) ? 'FG' : 'RM')
const wibStamp = (value: string) =>
  new Date(new Date(value).getTime() + 7 * 3600_000).toISOString().slice(0, 16).replace('T', ' ')

/* ============================================================ MST-11 Master Unit */

export const UNIT_KIND_LABEL: Record<string, string> = { PACK: 'Kemasan', SMALL: 'Satuan Kecil' }
const UNIT_KIND_ORDER = ['PACK', 'SMALL']

/** Tempat satuan "berdiri": material (satuan beli & satuan kecil), kemasan per supplier, dan baris BOM. */
function unitPlaces(unitId: string) {
  let places = 0
  const materials = new Set<string>()
  const materialCode = (id: unknown) => String(findRow('mt_material', idOf(id))?.code ?? '')
  readTable('mt_material').forEach(material => {
    if (idOf(material.default_purchase_unit_id) === unitId) {
      places += 1
      materials.add(String(material.code))
    }
    if (idOf(material.unit_small_id) === unitId) {
      places += 1
      materials.add(String(material.code))
    }
  })
  readTable('mt_material_pack_standard').forEach(pack => {
    if (idOf(pack.unit_big_id) !== unitId) return
    places += 1
    materials.add(materialCode(pack.material_id))
  })
  readTable('mt_bill_of_material').forEach(line => {
    if (idOf(line.unit_id) !== unitId) return
    places += 1
    materials.add(materialCode(line.material_id))
  })
  return { places, materials: [...materials].filter(Boolean).sort() }
}

function unitUsage(row: LocalRow): Usage {
  const { places, materials } = unitPlaces(row.id)
  const usage = usageFromCounts([['tempat', places]])
  const shown = materials.slice(0, 4).join(', ')
  return { ...usage, sentence: places ? `${places} tempat (${shown}${materials.length > 4 ? ', …' : ''})` : '' }
}

registerEntity('unit', {
  collection: 'mt_unit',
  entity: 'Satuan',
  page: 'MST-11',
  wh: 'RM',
  labelOf: row => String(row.name),
  usage: unitUsage,
  deletable: false
})

const unitRow = (row: LocalRow) => ({
  id: row.id,
  code: String(row.code),
  name: String(row.name),
  kind: String(row.kind),
  kind_label: UNIT_KIND_LABEL[String(row.kind)] ?? String(row.kind),
  description: (row.description as string | null) ?? null,
  is_active: row.is_active !== false,
  inactive_reason: (row.inactive_reason as string | null) ?? null
})

export function unitList() {
  const rows = readTable('mt_unit')
    .slice()
    .sort((a, b) => {
      const byKind = UNIT_KIND_ORDER.indexOf(String(a.kind)) - UNIT_KIND_ORDER.indexOf(String(b.kind))
      return byKind || String(a.name).localeCompare(String(b.name))
    })
    .map(row => ({ ...unitRow(row), places: unitPlaces(row.id).places }))
  return {
    rows,
    total: rows.length,
    active: rows.filter(row => row.is_active).length,
    can_write: canWrite('MST-11')
  }
}

export function unitDetail(params?: { id?: string }) {
  const row = findRow('mt_unit', text(params?.id))
  if (!row) fail('Satuan tidak ditemukan atau sudah tidak ada.', 'ERR_NOT_FOUND', 404)
  const { places, materials } = unitPlaces(row.id)
  return { ...unitRow(row), places, materials, can_write: canWrite('MST-11') }
}

type UnitSave = { id?: string; name?: string; kind?: string; description?: string }

export function unitSave(body: UnitSave) {
  requireWrite('MST-11', 'Menyunting satuan hanya untuk Admin WH RM, WH RM Supervisor, dan Kadiv WH RM.')
  const name = text(body.name)
  const kind = text(body.kind) || 'PACK'
  const description = text(body.description)
  if (!name) fail('Nama satuan wajib diisi.')
  if (name.length > 20) fail('Nama satuan maksimal 20 karakter.')
  if (!UNIT_KIND_ORDER.includes(kind)) fail('Pilih kategori Kemasan atau Satuan Kecil.')
  if (description.length > 120) fail('Keterangan maksimal 120 karakter.')
  const existing = body.id ? findRow('mt_unit', body.id) : undefined
  if (body.id && !existing) fail('Satuan tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  /* Pembanding huruf kecil: "Box" dan "box" adalah satuan yang sama — justru itu yang dicegah master ini. */
  const twin = readTable('mt_unit').find(row => sameName(row.name, name) && row.id !== existing?.id)
  if (twin) fail(`Satuan "${twin.name}" sudah ada di kategori ${UNIT_KIND_LABEL[String(twin.kind)] ?? twin.kind}.`)
  if (existing) {
    const updated = updateRow('mt_unit', existing.id, {
      ...auditFieldsForUpdate(),
      name,
      kind,
      description: description || null
    }) as LocalRow
    masterLog(
      'RM',
      'MST-11',
      'unit-ubah',
      String(existing.code),
      `${existing.name} → ${name} (${UNIT_KIND_LABEL[kind]})`
    )
    return unitRow(updated)
  }
  const code = name.toUpperCase().replace(/[^A-Z0-9]+/g, '')
  if (readTable('mt_unit').some(row => String(row.code).toUpperCase() === code))
    fail(`Kode satuan ${code} sudah dipakai satuan lain.`)
  const created = insertRow('mt_unit', {
    ...auditFieldsForCreate(),
    id: newId(),
    code,
    name,
    kind,
    description: description || null,
    inactive_reason: null,
    is_active: true
  })
  masterLog(
    'RM',
    'MST-11',
    'unit-tambah',
    code,
    `${name} (${UNIT_KIND_LABEL[kind]})${description ? ` — ${description}` : ''}`
  )
  return unitRow(created)
}

/* ============================================================ MST-09 Master Alasan NG */

/** Kategori ERD `mt_reject_type.categories` beserta layar yang memakainya. */
export const REASON_CATEGORIES: { code: string; label: string; hint: string }[] = [
  { code: 'FG_CLAIM', label: 'Claim customer', hint: 'Arsip — v7.50 tidak lagi meminta alasan per claim' },
  { code: 'FG_REPACK', label: 'Repack FG', hint: 'Sortir claim — alasan per unit' },
  { code: 'FG_NG', label: 'Not Good FG', hint: 'Sortir claim — alasan per unit' },
  { code: 'RM_FINDING', label: 'Hasil opname & temuan harian', hint: 'Opname RM, temuan NG gudang' },
  { code: 'RM_QC', label: 'QC kedatangan & retur supplier', hint: 'QC barang RM, retur ke supplier' }
]
const categoryLabel = (code: string) => REASON_CATEGORIES.find(item => item.code === code)?.label ?? code

registerEntity('reason', {
  collection: 'mt_reject_type',
  entity: 'Alasan',
  page: 'MST-09',
  wh: null,
  labelOf: row => String(row.name),
  usage: () => usageFromCounts([]),
  deletable: false
})

const reasonRow = (row: LocalRow) => {
  const categories = (row.categories as string[] | undefined) ?? []
  return {
    id: row.id,
    code: String(row.code),
    name: String(row.name),
    description: (row.description as string | null) ?? null,
    categories,
    category_labels: categories.map(categoryLabel),
    is_active: row.is_active !== false,
    inactive_reason: (row.inactive_reason as string | null) ?? null
  }
}

export function reasonList() {
  const rows = readTable('mt_reject_type')
    .slice()
    .sort((a, b) => num(a.sort) - num(b.sort))
    .map(reasonRow)
  return {
    rows,
    total: rows.length,
    active: rows.filter(row => row.is_active).length,
    categories: REASON_CATEGORIES,
    can_write: canWrite('MST-09')
  }
}

export function reasonDetail(params?: { id?: string }) {
  const row = findRow('mt_reject_type', text(params?.id))
  if (!row) fail('Alasan tidak ditemukan atau sudah tidak ada.', 'ERR_NOT_FOUND', 404)
  return { ...reasonRow(row), can_write: canWrite('MST-09') }
}

type ReasonSave = { id?: string; name?: string; description?: string; categories?: string[] }

export function reasonSave(body: ReasonSave) {
  requireWrite('MST-09', 'Menyunting alasan hanya untuk Admin, Supervisor, dan Kadiv gudang.')
  const name = text(body.name)
  const description = text(body.description)
  const categories = [...new Set((body.categories ?? []).map(text))].filter(Boolean)
  if (!name) fail('Nama alasan wajib diisi.')
  if (!categories.length)
    fail('Pilih minimal satu kategori — kalau tidak, alasan ini tidak akan muncul di layar mana pun.')
  const known = REASON_CATEGORIES.map(item => item.code)
  if (categories.some(code => !known.includes(code))) fail('Kategori tidak dikenal.')
  const existing = body.id ? findRow('mt_reject_type', body.id) : undefined
  if (body.id && !existing) fail('Alasan tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  /* Nama yang sama boleh hidup di kategori berbeda (mis. "Penyok" untuk claim dan Not Good); kembar = nama sama + kategori beririsan. */
  const twin = readTable('mt_reject_type').find(
    row =>
      sameName(row.name, name) &&
      row.id !== existing?.id &&
      ((row.categories as string[] | undefined) ?? []).some(code => categories.includes(code))
  )
  if (twin) fail(`Alasan "${name}" sudah ada.`)
  const world = categories.every(code => code.startsWith('RM_'))
    ? 'RM'
    : categories.every(code => code.startsWith('FG_'))
      ? 'FG'
      : null
  if (existing) {
    const updated = updateRow('mt_reject_type', existing.id, {
      ...auditFieldsForUpdate(),
      name,
      description: description || null,
      categories
    }) as LocalRow
    masterLog(world, 'MST-09', 'alasan-ubah', String(existing.code), `${existing.name} → ${name}`)
    return reasonRow(updated)
  }
  const next = readTable('mt_reject_type').filter(row => String(row.code).startsWith('ALS-')).length + 1
  const created = insertRow('mt_reject_type', {
    ...auditFieldsForCreate(),
    id: newId(),
    code: `ALS-${pad(next, 3)}`,
    name,
    description: description || null,
    categories,
    sort: readTable('mt_reject_type').reduce((max, row) => Math.max(max, num(row.sort)), 0) + 1,
    inactive_reason: null,
    is_active: true
  })
  masterLog(world, 'MST-09', 'alasan-tambah', String(created.code), `${name}${description ? ` — ${description}` : ''}`)
  return reasonRow(created)
}

/* ============================================================ MST-08 Master SLA */

export const SLA_GROUP_LABEL: Record<string, string> = {
  INBOUND_FG: 'Inbound FG',
  OUTBOUND_FG: 'Order Pengiriman FG',
  MERGE_ALM: 'Penggabungan ALM',
  CLAIM: 'Claim Customer',
  DESTRUCTION_FG: 'Pemusnahan FG',
  INBOUND_RM: 'Inbound RM',
  STOCK_OPNAME: 'Stock Opname'
}
export const SLA_UNIT_LABEL: Record<SlaUnit, string> = { HOUR: 'jam', MINUTE: 'menit', DAY: 'hari', PERCENT: '%' }
/** Ambang khusus Admin: Supervisor justru penerima wewenang saat ambangnya lewat, jadi ia tidak mengatur ambangnya sendiri. */
const SLA_ADMIN_ONLY: Record<string, string[]> = { GAB_ALM_AUTO_APPROVE: ['admin_wh_fg'] }

/** Peta pemakaian — supaya jelas mengubah satu angka berdampak ke mana. [kode, halaman, pengaruh]. */
const SLA_USAGE: [string, string, string][] = [
  ['FG_APPROVE_REMIND', 'Penerimaan FG', 'Batch diberi tanda kuning dan Supervisor diingatkan'],
  [
    'FG_APPROVE_ESCALATE',
    'Penerimaan FG',
    'Wewenang keputusan naik ke Kadiv WH FG (Setujui / Tahan / Kembalikan atas disposisi). Dimatikan: tidak pernah naik ke Kadiv, keputusan tetap di Supervisor'
  ],
  [
    'FG_APPROVE_AUTO',
    'Penerimaan FG',
    'Batch otomatis DISETUJUI bila tidak ada keputusan — final. Dimatikan: batch menunggu keputusan manual'
  ],
  [
    'FG_RECEIVE_LIMIT',
    'Penerimaan FG · Dashboard Laporan · Dashboard Utama',
    'Kartu "batch lewat tenggat" pada papan kerja dan dashboard'
  ],
  [
    'GAB_ALM_AUTO_APPROVE',
    'Permintaan Gabungan · Penggabungan — Operator ALM · Dashboard Utama',
    'Permintaan ALM yang belum diputuskan Admin sampai batas ini DISETUJUI otomatis (Supervisor hanya diberi tahu); hitung mundur tampil di dua layar. Dimatikan: menunggu Admin'
  ],
  [
    'OFG_RELEASE_ESCALATE',
    'Detail Order Pengiriman FG',
    'Release (termasuk bongkar) naik ke Kadiv WH FG, jam dinding, tidak pernah otomatis. Dimatikan: tidak pernah naik ke Kadiv, keputusan tetap di Supervisor'
  ],
  [
    'CLM_AUTO_APPROVE',
    'Detail & Proses Claim',
    'Hasil sortir otomatis DISETUJUI bila Supervisor belum memutuskan; cakupan menentukan Not Good ikut atau tidak. Dimatikan: menunggu Supervisor'
  ],
  [
    'DSP_APPROVE_ESCALATE',
    'Pemusnahan FG',
    'Ambang disposisi keputusan Not Good ke Kadiv WH FG. Belum ada layar yang memakainya: v7.50 menghapus jalur Kadiv di claim dan Kadiv tidak menyetujui pemusnahan (regulasi cukai). Dimatikan: tidak pernah naik ke Kadiv'
  ],
  ['QUARANTINE_MAX_AGE', 'QC Barang RM · Dashboard Laporan', 'Garis ambang pada grafik umur karantina'],
  [
    'ROP_BUFFER',
    'Master Raw Material · Daftar PR · Stock Inventory',
    'Hari cadangan di atas lead time terlama pada usulan Reorder Point'
  ],
  [
    'OPNAME_TARGET_MINUTES',
    'Laporan Stock Opname',
    'Penilaian sesi opname selesai tepat waktu atau lewat target (bila sesi tidak punya target sendiri)'
  ],
  ['OPNAME_MATCHED_TARGET', 'Laporan Stock Opname', 'Garis target pada grafik tren akurasi antar sesi'],
  [
    'DSC_ESCALATE_RM',
    'Selisih Opname (RM) · Detail Selisih',
    'Selisih Open lewat H+n dieskalasi otomatis ke Kadiv WH RM. Dimatikan: menunggu eskalasi manual'
  ],
  ['DSC_APPROVAL_RM', 'Selisih Opname (RM)', 'Target approval Kadiv WH RM (penanda tenggat)'],
  [
    'DSC_ESCALATE_FG',
    'Selisih Opname (FG) · Detail Selisih',
    'Selisih Open lewat H+n dieskalasi otomatis ke Kadiv WH FG. Dimatikan: menunggu eskalasi manual'
  ],
  ['DSC_APPROVAL_FG', 'Selisih Opname (FG)', 'Target approval Kadiv WH FG (penanda tenggat)']
]

const canEditSla = (code: string) => {
  if (!canWrite('MST-08')) return false
  const only = SLA_ADMIN_ONLY[code]
  return !only || myRoles().some(role => only.includes(role))
}

const slaRow = (row: LocalRow) => {
  const unit = String(row.unit) as SlaUnit
  const code = String(row.code)
  const meta = slaMetaOf(code, unit)
  const stored = (row.scope_json ?? {}) as Record<string, boolean>
  return {
    code,
    name: String(row.name),
    description: String(row.description ?? ''),
    group_code: String(row.group_code),
    group_label: SLA_GROUP_LABEL[String(row.group_code)] ?? String(row.group_code),
    unit,
    unit_label: SLA_UNIT_LABEL[unit] ?? unit,
    value: num(row.value),
    default_value: num(row.default_value),
    changed_reason: (row.changed_reason as string | null) ?? null,
    can_edit: canEditSla(code),
    admin_only: Boolean(SLA_ADMIN_ONLY[code]),
    /** Aturan otomatis yang boleh dimatikan; `is_enabled` selalu true untuk baris ambang biasa. */
    can_toggle: meta.toggle,
    is_enabled: meta.toggle ? row.is_enabled !== false : true,
    units: meta.units.map(item => ({ value: item, label: SLA_UNIT_LABEL[item] })),
    min_minutes: meta.units[0] === 'PERCENT' ? null : meta.min,
    max_minutes: meta.units[0] === 'PERCENT' || meta.max === Number.MAX_SAFE_INTEGER ? null : meta.max,
    range_label: rangeLabel(meta, unit),
    scope: (meta.scope ?? []).map(def => ({ ...def, value: stored[def.key] ?? def.default }))
  }
}

const UNIT_MINUTES_OF: Record<SlaUnit, number> = { MINUTE: 1, HOUR: 60, DAY: 1440, PERCENT: 1 }
/** Rentang ditulis dalam satuan baris bila habis dibagi; bila tidak (mis. batas bawah 1 menit pada baris jam) turun ke menit. */
const fmtSpan = (minutes: number, unit: SlaUnit) => {
  const shown = unit !== 'PERCENT' && minutes % UNIT_MINUTES_OF[unit] === 0 ? unit : 'MINUTE'
  return `${Math.round(minutes / UNIT_MINUTES_OF[shown]).toLocaleString('id-ID')} ${SLA_UNIT_LABEL[shown]}`
}
/** Rentang yang diizinkan, ditampilkan sebagai petunjuk di bawah isian. */
function rangeLabel(meta: SlaMeta, unit: SlaUnit) {
  if (unit === 'PERCENT') return `1 – ${meta.max} %`
  if (meta.max === Number.MAX_SAFE_INTEGER) return ''
  const shown = meta.units.includes(unit) ? unit : meta.units[0]
  return `${fmtSpan(meta.min, shown)} – ${fmtSpan(meta.max, shown)}`
}

export function slaList() {
  const rows = readTable('sla_wms_setting')
    .slice()
    .sort((a, b) => num(a.sort) - num(b.sort))
    .map(slaRow)
  const groups: { code: string; label: string; rows: typeof rows }[] = []
  rows.forEach(row => {
    let group = groups.find(item => item.code === row.group_code)
    if (!group) {
      group = { code: row.group_code, label: row.group_label, rows: [] }
      groups.push(group)
    }
    group.rows.push(row)
  })
  const byCode = new Map(rows.map(row => [row.code, row]))
  const usage = SLA_USAGE.map(([code, pages, effect]) => {
    const row = byCode.get(code)
    return {
      code,
      name: row?.name ?? code,
      value_label: row
        ? `${row.value.toLocaleString('id-ID')} ${row.unit_label}${row.can_toggle && !row.is_enabled ? ' · nonaktif' : ''}`
        : '—',
      pages,
      effect
    }
  })
  return { groups, rows, usage, can_write: canWrite('MST-08') }
}

type SlaChange = {
  code: string
  value?: number
  unit?: SlaUnit
  is_enabled?: boolean
  scope?: Record<string, boolean>
}
type SlaSave = { changes?: SlaChange[]; reason?: string }

const yesNo = (value: boolean) => (value ? 'Ya' : 'Tidak')

/** Nilai akhir satu baris sesudah perubahan diterapkan (belum disimpan). */
function slaNext(row: LocalRow, item: SlaChange) {
  const meta = slaMetaOf(String(row.code), String(row.unit) as SlaUnit)
  const stored = (row.scope_json ?? {}) as Record<string, boolean>
  const scope: Record<string, boolean> = {}
  ;(meta.scope ?? []).forEach(def => {
    scope[def.key] =
      item.scope && def.key in item.scope ? Boolean(item.scope[def.key]) : (stored[def.key] ?? def.default)
  })
  return {
    meta,
    value: item.value === undefined ? num(row.value) : Number(item.value),
    unit: (item.unit ?? row.unit) as SlaUnit,
    enabled: item.is_enabled === undefined ? row.is_enabled !== false : Boolean(item.is_enabled),
    scope
  }
}

function slaValidate(row: LocalRow, next: ReturnType<typeof slaNext>, item: SlaChange) {
  const { meta, value, unit } = next
  const name = String(row.name)
  if (item.is_enabled !== undefined && !meta.toggle) fail(`"${name}" bukan aturan otomatis, tidak dapat dimatikan.`)
  if (item.unit !== undefined && !meta.units.includes(item.unit))
    fail(`Satuan "${name}" harus ${meta.units.map(unitItem => SLA_UNIT_LABEL[unitItem]).join(' atau ')}.`)
  if (item.scope) {
    const known = new Set((meta.scope ?? []).map(def => def.key))
    if (Object.keys(item.scope).some(key => !known.has(key))) fail(`Opsi cakupan "${name}" tidak dikenal.`)
  }
  if (!(value > 0) && !(value === 0 && meta.allowZero)) fail(`Nilai "${name}" harus lebih besar dari nol.`)
  if (!Number.isInteger(value)) fail(`Nilai "${name}" harus bilangan bulat.`)
  if (unit === 'PERCENT') {
    if (value > 100) fail(`Nilai "${name}" tidak boleh lebih dari 100.`)
    return
  }
  const minutes = value * UNIT_MINUTES_OF[unit]
  if (minutes < meta.min || minutes > meta.max)
    fail(
      `Nilai "${name}" harus antara ${fmtSpan(meta.min, unit)} dan ${fmtSpan(meta.max, unit)}.`,
      'ERR_SLA_OUT_OF_RANGE'
    )
}

export function slaSave(body: SlaSave) {
  requireWrite('MST-08', 'Mengubah ambang SLA hanya untuk Admin, Supervisor, dan Kadiv gudang.')
  const changes = (body.changes ?? []).filter(item => item && item.code)
  const rows = readTable('sla_wms_setting')
  const picked = changes
    .map(item => {
      const row = rows.find(candidate => candidate.code === item.code)
      return row ? { item, row, next: slaNext(row, item) } : null
    })
    .filter((entry): entry is { item: SlaChange; row: LocalRow; next: ReturnType<typeof slaNext> } => Boolean(entry))
    .filter(({ row, next }) => {
      const meta = next.meta
      const stored = (row.scope_json ?? {}) as Record<string, boolean>
      const scopeChanged = (meta.scope ?? []).some(def => next.scope[def.key] !== (stored[def.key] ?? def.default))
      return (
        next.value !== num(row.value) ||
        next.unit !== row.unit ||
        (meta.toggle && next.enabled !== (row.is_enabled !== false)) ||
        scopeChanged
      )
    })
  if (!picked.length) fail('Tidak ada perubahan untuk disimpan.')
  for (const { item, row, next } of picked) {
    slaValidate(row, next, item)
    if (!canEditSla(String(row.code)))
      fail(`"${row.name}" hanya dapat diubah Admin WH FG.`, 'ERR_UNAUTHORIZED_ROLE', 403)
  }
  const reason = text(body.reason)
  if (!reason) fail('Alasan perubahan wajib diisi.')
  const trail = picked.map(({ row, next }) => {
    const parts: string[] = []
    const unitFrom = SLA_UNIT_LABEL[String(row.unit) as SlaUnit] ?? row.unit
    if (next.value !== num(row.value) || next.unit !== row.unit)
      parts.push(`${num(row.value)} ${unitFrom} → ${next.value} ${SLA_UNIT_LABEL[next.unit] ?? next.unit}`)
    if (next.meta.toggle && next.enabled !== (row.is_enabled !== false))
      parts.push(`${row.is_enabled !== false ? 'Aktif' : 'Nonaktif'} → ${next.enabled ? 'Aktif' : 'Nonaktif'}`)
    const stored = (row.scope_json ?? {}) as Record<string, boolean>
    ;(next.meta.scope ?? []).forEach(def => {
      const before = stored[def.key] ?? def.default
      if (before !== next.scope[def.key]) parts.push(`${def.label}: ${yesNo(before)} → ${yesNo(next.scope[def.key])}`)
    })
    return `${row.name}: ${parts.join(', ')}`
  })
  picked.forEach(({ row, next }) =>
    updateRow('sla_wms_setting', row.id, {
      ...auditFieldsForUpdate(),
      value: next.value,
      unit: next.unit,
      ...(next.meta.toggle ? { is_enabled: next.enabled } : {}),
      ...(next.meta.scope ? { scope_json: next.scope } : {}),
      changed_reason: reason
    })
  )
  masterLog(worldOf(), 'MST-08', 'sla-ubah', 'sla_wms_setting', `${trail.join(' · ')} — ALASAN: ${reason}`)
  return { changed: picked.length, trail }
}

/** Nilai bawaan seed (tombol "Kembalikan" tidak menyentuh server — ini hanya rujukan untuk tes & dokumentasi). */
export const SLA_DEFAULTS = Object.fromEntries(SLA_DEFS.map(def => [def[0], def[4]]))

/* ============================================================ MST-10 Master Pallet Karantina */

const PALLET_KIND = 'RM_QUARANTINE'
const PALLET_MAX = 50
const PALLET_CODE = /^PLT-(\d+)$/

const quarantinePallets = () => readTable('pallet').filter(row => row.pallet_kind === PALLET_KIND)

/** Nomor QR pallet = "PLT-" + 6 digit dengan penghitung sendiri (bukan penghitung kode material). */
export function nextPalletNumber() {
  return (
    readTable('pallet').reduce((max, row) => {
      const match = PALLET_CODE.exec(String(row.code))
      return match ? Math.max(max, Number(match[1])) : max
    }, 0) + 1
  )
}
const palletCode = (value: number) => `PLT-${pad(value, 6)}`

function palletState(row: LocalRow) {
  if (row.pallet_status === 'IN_QC') return { state: 'BUSY', label: 'Sedang QC / Putaway' }
  const hasContent = readTable('rm_pallet_content').some(content => idOf(content.pallet_id) === row.id)
  return hasContent ? { state: 'PARTIAL', label: 'Terisi sebagian' } : { state: 'EMPTY', label: 'Kosong' }
}

const palletRow = (row: LocalRow) => {
  const status = palletState(row)
  const cycles = num(row.cycle_count)
  return {
    id: row.id,
    code: String(row.code),
    created_at: wibStamp(String(row.date_created)),
    state: status.state,
    state_label: status.label,
    cycle_count: cycles,
    cycle_label: cycles ? `${cycles}× siklus` : 'baru'
  }
}

registerEntity('pallet', {
  collection: 'pallet',
  entity: 'Pallet',
  page: 'MST-10',
  wh: 'RM',
  labelOf: row => String(row.code),
  usage: () => usageFromCounts([]),
  deletable: false
})

export function palletList() {
  const rows = quarantinePallets()
    .slice()
    .sort((a, b) => String(b.code).localeCompare(String(a.code)))
    .map(palletRow)
  return {
    rows,
    kpi: {
      total: rows.length,
      empty: rows.filter(row => row.state !== 'BUSY').length,
      busy: rows.filter(row => row.state === 'BUSY').length,
      reused: rows.filter(row => row.cycle_count > 0).length
    },
    next_code: palletCode(nextPalletNumber()),
    max_per_batch: PALLET_MAX,
    can_generate: canWrite('MST-10')
  }
}

export function palletGenerate(body: { qty?: number }) {
  requireWrite('MST-10', 'Menambah pallet hanya untuk Admin WH RM.')
  const qty = Number(body.qty)
  if (!Number.isInteger(qty) || qty < 1 || qty > PALLET_MAX) fail(`Jumlah pallet 1–${PALLET_MAX}.`)
  const start = nextPalletNumber()
  const created = Array.from({ length: qty }, (_, index) =>
    insertRow('pallet', {
      ...auditFieldsForCreate(),
      id: newId(),
      code: palletCode(start + index),
      pallet_kind: PALLET_KIND,
      zone_id: null,
      capacity: null,
      recommended: null,
      unit_type: null,
      description: 'QR Pallet Karantina (reusable)',
      pallet_status: 'EMPTY',
      cycle_count: 0,
      is_active: true
    })
  )
  masterLog(
    'RM',
    'MST-10',
    'print-pallet',
    created.map(row => String(row.code)).join(', '),
    `${qty} QR Pallet kosong digenerate & dicetak`,
    'RM Receiving'
  )
  return { created: created.map(palletRow), next_code: palletCode(start + qty) }
}

/** Pratinjau konfirmasi "Generate n QR Pallet?" — mulai dari nomor berikut. */
export function palletPreview(params?: { qty?: number | string }) {
  const qty = Number(params?.qty)
  return { qty, start: palletCode(nextPalletNumber()), valid: Number.isInteger(qty) && qty >= 1 && qty <= PALLET_MAX }
}

/* ============================================================ register */

export function registerMasterSimpleHandlers(mock: MockAdapter) {
  const base = '/api/v1/wms/master'
  const get = (path: string, run: (params: Record<string, unknown> | undefined) => unknown) =>
    mock
      .onGet(`${base}/${path}`)
      .reply(config => handle(() => run(config.params as Record<string, unknown> | undefined)))
  const post = <T>(path: string, run: (body: T) => unknown) =>
    mock.onPost(`${base}/${path}`).reply(config => handle(() => run(parse<T>(config.data))))
  get('unit/list', () => unitList())
  get('unit/detail', params => unitDetail(params as { id?: string }))
  post('unit/save', unitSave)
  get('reason/list', () => reasonList())
  get('reason/detail', params => reasonDetail(params as { id?: string }))
  post('reason/save', reasonSave)
  get('sla/list', () => slaList())
  post('sla/save', slaSave)
  get('pallet/list', () => palletList())
  get('pallet/preview', params => palletPreview(params as { qty?: number }))
  post('pallet/generate', palletGenerate)
}
